/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

export const POLITICAL_PREVIEW_RENDER_TEMPLATE = `<a id="{{adId}}" rel="nofollow" target="_blank" href="{{{urls.destination.url}}}">
<img src="{{{assets.Image.cdnId}}}" width="{{creativeWidth}}" height="{{creativeHeight}}" alt=""/>
</a>
{{{include.attention}}}

{{{politicalOverlay}}}

<style>
    .adn-ttpa-container,
    .adn-ttpa-container * {
        line-height: 1.2em !important;
    }
</style>`;

export const POLITICAL_PREVIEW_FULL_SCREEN_RENDER_TEMPLATE = `<script>
  window.aim = window.aim || {};
  window.aim.format="fullScreen";
  (function() {
    try {
      var dummyFrame = document.createElement('iframe');
      Object.defineProperty(window, 'frameElement', {configurable: true, get: function() { return dummyFrame; }});
    } catch (e) {}
  })();
</script>
<script src="https://assets.adnuntius.com/aim.js"></script>
<style>
	html, body {
		margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        height: 100% !important;
        overflow: hidden !important;
	}
    .responseCtr,
    .contentWrapperPerItem {
        display: block !important;
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        overflow: hidden !important;
    }
	.backgroundImage {
    	width: 100% !important;
        height: 100% !important;
        display: block !important;
        overflow: hidden;
        background-position: 50% 50%;
        background-size: cover;
        background-repeat: no-repeat;
        background-image: url({{{assets.Image.cdnId}}});
    }
    .backgroundImage img {
        width: 100% !important;
        height: 100% !important;
        max-width: none !important;
        max-height: none !important;
        object-fit: cover !important;
        display: block !important;
        border: 0;
    }
    .adn-ttpa-container {
    	top: 1px;
        bottom: auto !important;
    }
</style>
<style>
    .adn-ttpa-container,
    .adn-ttpa-container * {
        line-height: 1.2em !important;
    }
</style>
<a href="{{{urls.destination.url}}}" class="backgroundImage" rel="nofollow" target="_blank" style="background-image: url({{{assets.image.cdnId}}})">
  <img src="{{{assets.Image.cdnId}}}" alt="">
</a>

<script>
  (function() {
    var fill = function() {
      var setFill = function(el) {
        if (!el || !el.style) {
          return;
        }
        el.style.setProperty('display', 'block', 'important');
        el.style.setProperty('width', '100%', 'important');
        el.style.setProperty('height', '100%', 'important');
        el.style.setProperty('max-width', 'none', 'important');
        el.style.setProperty('max-height', 'none', 'important');
        el.style.setProperty('margin', '0', 'important');
        el.style.setProperty('padding', '0', 'important');
      };
      setFill(document.documentElement);
      setFill(document.body);
      var nodes = document.querySelectorAll('.responseCtr, .contentWrapperPerItem, .backgroundImage');
      for (var i = 0; i < nodes.length; i++) {
        setFill(nodes[i]);
      }
      var imgs = document.querySelectorAll('.backgroundImage img');
      for (var j = 0; j < imgs.length; j++) {
        imgs[j].removeAttribute('width');
        imgs[j].removeAttribute('height');
        imgs[j].style.setProperty('width', '100%', 'important');
        imgs[j].style.setProperty('height', '100%', 'important');
        imgs[j].style.setProperty('object-fit', 'cover', 'important');
        imgs[j].style.setProperty('display', 'block', 'important');
      }
    };
    fill();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fill);
    }
    window.addEventListener('load', fill);
  })();
</script>

{{{politicalOverlay}}}`;

export function getPoliticalPreviewRenderTemplate(layout: string): string {
  return layout === 'fullScreen' ? POLITICAL_PREVIEW_FULL_SCREEN_RENDER_TEMPLATE : POLITICAL_PREVIEW_RENDER_TEMPLATE;
}

export function getPoliticalPreviewImageUrl(form: any): string {
  if (form.imageSource === 'upload' && form.uploadedImageContent) {
    return form.uploadedImageContent;
  }
  if (form.imageSource === 'hosted' && form.hostedImageUrl) {
    return form.hostedImageUrl;
  }
  return form.uploadedImageContent || form.hostedImageUrl || '';
}

export function getPoliticalPreviewLayoutParameters(form: any): any {
  const imageUrl = getPoliticalPreviewImageUrl(form);
  const imageAsset = imageUrl ? {cdnId: imageUrl} : undefined;
  return _.pickBy({
    destination: form.destinationUrl,
    Image: imageAsset,
    image: imageAsset
  }, (value) => {
    return value !== undefined && value !== null && value !== '';
  });
}

export function formatPoliticalPreviewDate(value: any): string {
  if (!value) {
    return '';
  }
  if (_.isString(value)) {
    return value;
  }
  if (_.isDate(value) && !Number.isNaN(value.getTime())) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return year + '-' + month + '-' + day;
  }
  return String(value);
}

export function parsePoliticalPreviewDate(value: any): Date | '' {
  if (!value) {
    return '';
  }
  if (_.isDate(value) && !Number.isNaN(value.getTime())) {
    return value;
  }
  if (_.isString(value)) {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (match) {
      const year = Number(match[1]);
      const month = Number(match[2]) - 1;
      const day = Number(match[3]);
      return new Date(year, month, day);
    }
  }
  return '';
}

export function shouldLoadPoliticalPreviewElections(cachedElections: any): boolean {
  return !_.isArray(cachedElections) || cachedElections.length < 1;
}

export function getDefaultPoliticalPreviewElectionId(elections: any[]): string {
  return _.get(_.head(elections || []), 'id', '');
}

export function injectPoliticalPreviewHtml(
  previewHtml: string,
  destinationUrl: string,
  imageUrl: string,
  languageCode: string
): string {
  let html = previewHtml || '';

  if (languageCode) {
    if (/<html\b[^>]*\slang=/i.test(html)) {
      html = html.replace(/(<html\b[^>]*\slang=")([^"]*)(")/i, '$1' + languageCode + '$3');
    } else if (/<html\b/i.test(html)) {
      html = html.replace(/<html\b/i, '<html lang="' + languageCode + '"');
    }
  }

  html = html.replace(/(<a\b[^>]*\shref=")([^"]*)(")/gi, function(_match, prefix, href, suffix) {
    if (!href || !/\/(?:ad-)?transparency-preview(?:[/?#]|$)/.test(href)) {
      return prefix + href + suffix;
    }

    const missingParams = [];

    if (href.indexOf('political=') < 0) {
      missingParams.push('political=true');
    }

    if (href.indexOf('preview=') < 0) {
      missingParams.push('preview=true');
    }

    if (!missingParams.length) {
      return prefix + href + suffix;
    }

    const joiner = href.indexOf('?') > -1 ? '&' : '?';
    return prefix + href + joiner + missingParams.join('&') + suffix;
  });

  if (destinationUrl) {
    html = html.replace(/\{\{\{urls\.destination\.url\}\}\}/g, destinationUrl);
    if (/<a\b[^>]*href=/i.test(html)) {
      html = html.replace(/(<a\b[^>]*\shref=")([^"]*)(")/i, '$1' + destinationUrl + '$3');
    } else if (/<a\b/i.test(html)) {
      html = html.replace(/<a\b/i, '<a href="' + destinationUrl + '"');
    }
  }

  if (imageUrl) {
    html = html.replace(/\{\{\{assets\.(?:Image|image)\.cdnId\}\}\}/g, imageUrl);
    html = html.replace(/(background-image\s*:\s*url\()(['"]?)(?:\{\{\{[^)]*\}\}\}|)\2(\))/gi, '$1$2' + imageUrl + '$2$3');
    html = html.replace(/(<img\b[^>]*\ssrc=")(?:\{\{\{[^"]*\}\}\}|)?(")/gi, '$1' + imageUrl + '$2');
  }

  return html;
}

export function findPoliticalOverlayElement(document: Document | null): Element | null {
  if (!document || !document.querySelector) {
    return null;
  }

  return document.querySelector('.adn-ttpa-container, [data-testid="adn-ttpa-container"], [id="adn-ttpa-container"]');
}

function setPoliticalPreviewFillStyle(el: HTMLElement | null): void {
  if (!el || !el.style) {
    return;
  }
  el.style.setProperty('display', 'block', 'important');
  el.style.setProperty('width', '100%', 'important');
  el.style.setProperty('height', '100%', 'important');
  el.style.setProperty('max-width', 'none', 'important');
  el.style.setProperty('max-height', 'none', 'important');
  el.style.setProperty('margin', '0', 'important');
  el.style.setProperty('padding', '0', 'important');
}

export function expandPoliticalPreviewFullScreenDocument(doc: Document | null): void {
  if (!doc || !doc.documentElement) {
    return;
  }
  setPoliticalPreviewFillStyle(doc.documentElement);
  setPoliticalPreviewFillStyle(doc.body);
  _.forEach(doc.querySelectorAll('.responseCtr, .contentWrapperPerItem, .backgroundImage'), (el: HTMLElement) => {
    setPoliticalPreviewFillStyle(el);
  });
  _.forEach(doc.querySelectorAll('.backgroundImage img'), (img: HTMLImageElement) => {
    img.removeAttribute('width');
    img.removeAttribute('height');
    img.style.setProperty('width', '100%', 'important');
    img.style.setProperty('height', '100%', 'important');
    img.style.setProperty('object-fit', 'cover', 'important');
    img.style.setProperty('display', 'block', 'important');
  });
}
